'use client';

import React, { useState, useEffect } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Search, Layers, GitCompare, Briefcase } from 'lucide-react';
import Link from 'next/link';
import NextImage from 'next/image';

import { TopBanner } from './TopBanner';

interface NavbarProps {
    onDemoClick?: () => void;
}

const resourcesLinks = [
    { href: '/#agents', label: 'Agents', icon: Search, description: 'Meet our 5 AI agents' },
    { href: '/solutions', label: 'Solutions', icon: Layers, description: 'Industry-specific solutions' },
    { href: '/use-cases', label: 'Use Cases', icon: Briefcase, description: 'Real-world applications' },
    { href: '/compare', label: 'Compare', icon: GitCompare, description: 'See how we stack up' },
];

export const Navbar = ({ onDemoClick }: NavbarProps) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isResourcesOpen, setIsResourcesOpen] = useState(false);
    const [isMobileResourcesOpen, setIsMobileResourcesOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="fixed top-0 left-0 right-0 z-50 flex flex-col font-sans">
            <TopBanner />
            <m.nav
                className={`w-full transition-all duration-300 ${isScrolled
                    ? 'bg-white/80 backdrop-blur-md border-b border-neutral-200 py-3'
                    : 'bg-transparent py-5'
                    }`}
                initial={{ y: -100 }}
                animate={{ y: 0 }}
            >
                <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
                    <Link href="/" className="flex items-center gap-2 group">
                        <NextImage src='/axist.png' width={32} height={32} className='h-8 w-8 rounded-lg shadow-sm group-hover:scale-105 transition-transform' alt="Axist" unoptimized />
                        <span className="text-xl font-bold text-neutral-900 tracking-tight">Axist</span>
                    </Link>

                    {/* Desktop Menu */}
                    <div className="hidden md:flex items-center gap-8">
                        {/* Resources Dropdown */}
                        <div
                            className="relative"
                            onMouseEnter={() => setIsResourcesOpen(true)}
                            onMouseLeave={() => setIsResourcesOpen(false)}
                        >
                            <button
                                className="flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors"
                                onClick={() => setIsResourcesOpen((prev) => !prev)}
                                aria-haspopup="menu"
                                aria-controls="resources-dropdown"
                                aria-expanded={isResourcesOpen}
                            >
                                Resources
                                <ChevronDown size={16} className={`transition-transform ${isResourcesOpen ? 'rotate-180' : ''}`} />
                            </button>

                            <AnimatePresence>
                                {isResourcesOpen && (
                                    <m.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        transition={{ duration: 0.15 }}
                                        className="absolute top-full left-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-neutral-200 p-2 overflow-hidden"
                                        id="resources-dropdown"
                                    >
                                        {resourcesLinks.map((link) => {
                                            const Icon = link.icon;
                                            return (
                                                <Link
                                                    key={link.href}
                                                    href={link.href}
                                                    className="flex items-start gap-3 p-3 rounded-xl hover:bg-neutral-50 transition-colors group"
                                                >
                                                    <div className="p-2 rounded-lg bg-neutral-100 text-neutral-600 group-hover:bg-orange-100 group-hover:text-orange-600 transition-colors">
                                                        <Icon size={18} />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-semibold text-neutral-900">{link.label}</p>
                                                        <p className="text-xs text-neutral-500">{link.description}</p>
                                                    </div>
                                                </Link>
                                            );
                                        })}
                                    </m.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <Link href="/pricing" className="text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors">Pricing</Link>

                        <div className="flex items-center gap-4 ml-4">
                            <Link href="/login" className="text-sm font-medium text-neutral-900 hover:text-orange-600 transition-colors">
                                Log in
                            </Link>
                            <Link
                                href="/login?mode=signup"
                                className="px-5 py-2 bg-neutral-900 text-white text-sm font-medium rounded-full hover:bg-orange-600 transition-all shadow-sm hover:shadow-md cursor-pointer"
                            >
                                Start Free
                            </Link>
                        </div>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="md:hidden p-2 text-neutral-600 cursor-pointer"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                    >
                        {isMobileMenuOpen ? <X /> : <Menu />}
                    </button>
                </div>
            </m.nav>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <m.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="absolute top-full left-0 right-0 bg-white border-b border-neutral-100 z-40 md:hidden overflow-hidden shadow-xl"
                    >
                        <div className="p-6 space-y-4">
                            {/* Mobile Resources Dropdown */}
                            <div>
                                <button
                                    className="flex items-center justify-between w-full text-lg font-medium text-neutral-900"
                                    onClick={() => setIsMobileResourcesOpen(!isMobileResourcesOpen)}
                                >
                                    Resources
                                    <ChevronDown size={20} className={`transition-transform ${isMobileResourcesOpen ? 'rotate-180' : ''}`} />
                                </button>
                                <AnimatePresence>
                                    {isMobileResourcesOpen && (
                                        <m.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="mt-3 ml-4 space-y-3 overflow-hidden"
                                        >
                                            {resourcesLinks.map((link) => (
                                                <Link
                                                    key={link.href}
                                                    href={link.href}
                                                    className="block text-base font-medium text-neutral-600 hover:text-orange-600"
                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                >
                                                    {link.label}
                                                </Link>
                                            ))}
                                        </m.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            <Link
                                href="/pricing"
                                className="block text-lg font-medium text-neutral-900"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Pricing
                            </Link>
                            <Link
                                href="/customers"
                                className="block text-lg font-medium text-neutral-900"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Customers
                            </Link>
                            <Link
                                href="/faq"
                                className="block text-lg font-medium text-neutral-900"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                FAQ
                            </Link>
                            <hr className="border-neutral-100" />
                            <Link
                                href="/login"
                                className="block text-lg font-medium text-neutral-600"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Log in
                            </Link>
                            <Link
                                href="/login?mode=signup"
                                className="block w-full py-3 bg-neutral-900 text-white font-medium rounded-xl text-center"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Start Free
                            </Link>
                        </div>
                    </m.div>
                )}
            </AnimatePresence>
        </div>
    );
};
